import { useEffect, useState, type ReactNode } from 'react';
import { Command, Globe2, Pause, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DropdownSelect } from '@/components/ui/DropdownSelect';
import { ATLAS_LANGUAGES, useLanguage, type AtlasLanguage } from '@/lib/language-context';
import { appName } from '@/lib/app-surface';

export type AuthLanguage = AtlasLanguage;
export const AUTH_LANGUAGES = ATLAS_LANGUAGES;

export function useAuthLanguage() {
  return useLanguage();
}

/** Nature photographs for the image half, shown one after another. */
const IMAGES = ['/bg1.jpg', '/bg2.jpg', '/bg3.jpg', '/bg4.jpg'];
const SLIDE_MS = 7000;

function NatureSlides({ paused }: { paused: boolean }) {
  const [index, setIndex] = useState(0);
  // Load the rest only after the first image, so sign-in isn't slowed down.
  const [loaded, setLoaded] = useState(1);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % IMAGES.length), SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [paused]);

  return (
    <>
      {IMAGES.slice(0, loaded).map((src, position) => (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchPriority={position === 0 ? 'high' : 'low'}
          onLoad={() => position === 0 && setLoaded(IMAGES.length)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1600ms] ease-in-out ${position === index ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}
    </>
  );
}

/** Sign-in and sign-up frame: the form on one half, nature photographs on the other. */
export function AuthSplitLayout({
  children,
  language,
  onLanguageChange,
  topPrompt,
  topAction,
  topActionTo,
}: {
  children: ReactNode;
  language: AuthLanguage;
  onLanguageChange: (language: AuthLanguage) => void;
  topPrompt: string;
  topAction: string;
  topActionTo: string;
}) {
  const [canAnimate] = useState(() => !window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [paused, setPaused] = useState(!canAnimate);

  return (
    <main className="grid min-h-dvh bg-white text-neutral-950 lg:grid-cols-2">
      <section className="flex min-h-dvh flex-col px-6 py-5 sm:px-10">
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Command className="h-6 w-6 stroke-[1.5]" />
            <span className="text-sm font-medium tracking-tight">{appName}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-neutral-500">
            <span className="hidden sm:inline">{topPrompt}</span>
            <Link to={topActionTo} className="rounded-lg border border-neutral-200 px-3 py-1.5 font-medium text-neutral-950 transition hover:bg-neutral-50">
              {topAction}
            </Link>
          </div>
        </header>

        <div className="flex flex-1 items-center justify-center py-8">
          <div className="w-full max-w-[400px]">{children}</div>
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 pt-4">
          <nav className="flex gap-3 text-[11px] text-neutral-500" aria-label="Legal">
            <Link to="/privacy" className="hover:text-neutral-950 hover:underline">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-neutral-950 hover:underline">
              Terms
            </Link>
            <span className="hidden sm:inline">A HammerHead Technologies Inc. platform · © 2026</span>
          </nav>
          <div className="w-32">
            <DropdownSelect
              value={language}
              onChange={(value) => onLanguageChange(value as AuthLanguage)}
              ariaLabel="Language"
              leading={<Globe2 className="h-3.5 w-3.5 text-neutral-400" />}
              options={AUTH_LANGUAGES.map((option) => ({ ...option }))}
            />
          </div>
        </footer>
      </section>

      <aside className="relative hidden overflow-hidden bg-neutral-900 lg:block" aria-label="Nature photographs">
        <NatureSlides paused={paused} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" aria-hidden="true" />
        <img src="/HHTech1White.png" alt="Hammerhead Technologies" className="absolute bottom-8 left-8 h-auto w-40 drop-shadow" />
        {canAnimate && (
          <button
            type="button"
            onClick={() => setPaused((value) => !value)}
            aria-label={paused ? 'Play the photographs' : 'Pause the photographs'}
            className="absolute right-5 top-5 rounded-lg border border-white/30 bg-black/20 p-1.5 text-white backdrop-blur transition hover:bg-black/35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>
        )}
      </aside>
    </main>
  );
}
